// Grille paie — read-only: the paie version of each VALIDATED grille
// mensuelle, with the salaire habituel, the salaire du mois and the écart
// right after the name (before the days). Amounts come from the payroll
// month (same source as Salaires du mois); cells from /payroll/grid. A
// grille not yet validated is not shown at all.

import type { PayrollEmployeeDTO, PayrollGridCellType, PayrollGridDTO } from '@pointage/shared';
import { usePayrollGrid } from '../../api/hooks.js';
import { fmtDA } from '../../lib/format.js';
import { sbAvColor, sbInitials } from './common.js';

const DOW = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const CODE: Partial<Record<PayrollGridCellType, string>> = {
  recup: 'R', c: 'C', m: 'M', aj: 'AJ', anj: 'A', h: 'F', na: '–', need: '?',
};

// 8 → « 8h », 7.5 → « 7h30 »
function hrs(h: number): string {
  const m = Math.round(h * 60);
  const H = Math.floor(m / 60);
  const M = m % 60;
  return `${H}h${M ? String(M).padStart(2, '0') : ''}`;
}

function cellText(c: { type: PayrollGridCellType; hours: number | null }): string {
  if (c.type === 'p' || c.type === 'r') return c.hours != null ? hrs(c.hours) : '';
  if (c.type === 'tt') return 'TT';
  return CODE[c.type] ?? '';
}

function frDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function EntityGrid({
  ent,
  team,
  sent,
}: {
  ent: PayrollGridDTO['entities'][number];
  team: ReadonlyMap<string, PayrollEmployeeDTO>;
  sent: boolean;
}) {
  const rows = ent.rows.filter((r) => team.has(r.employeeId));
  if (rows.length === 0) return null;
  const paid = (e: PayrollEmployeeDTO) => (sent ? e.paidSalary ?? e.salary : e.salary);
  const count = (cells: Array<{ type: PayrollGridCellType }>, t: PayrollGridCellType) => cells.filter((c) => c.type === t).length;
  const sum = (f: (r: (typeof rows)[number], e: PayrollEmployeeDTO) => number) =>
    rows.reduce((a, r) => a + f(r, team.get(r.employeeId)!), 0);
  const totRef = sum((_, e) => e.referenceSalary);
  const totPaid = sum((_, e) => paid(e));

  return (
    <section className="sb-gp">
      <div className="sb-gp-head">
        <span className="sb-gp-ok">✓</span>
        <b>{ent.entityName}</b>
        <span className="sb-gp-meta">
          grille validée{ent.validatedBy ? ` par ${ent.validatedBy}` : ''}{ent.validatedAt ? ` le ${frDate(ent.validatedAt)}` : ''}
        </span>
      </div>
      <div className="gm-gridwrap">
        <div className="gm-scroll">
          <table className="sb-gptbl">
            <thead>
              <tr>
                <th className="sb-gp-emp">Employé <span className="gm-th-count">{rows.length}</span></th>
                <th className="sb-gp-sal sb-r">Salaire habituel</th>
                <th className="sb-gp-sal sb-r">Salaire du mois</th>
                <th className="sb-gp-sal sb-r">Écart</th>
                <th className="sb-gp-sal sb-gp-sep" title="Jours ouvrés non travaillés, retirés à 1/22">Non trav.</th>
                {ent.days.map((d) => (
                  <th key={d.date} className={'sb-gp-d' + (d.isWeekend ? ' off' : '')}>
                    <span className="sb-gp-dn gm-mono">{d.day}</span>
                    {DOW[d.dow]}
                  </th>
                ))}
                <th className="sb-gp-tot sb-r">Total h paie</th>
                <th className="sb-gp-cnt">C</th>
                <th className="sb-gp-cnt">M</th>
                <th className="sb-gp-cnt">AJ</th>
                <th className="sb-gp-cnt">A</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const e = team.get(r.employeeId)!;
                const diff = paid(e) - e.referenceSalary;
                const off = e.monthFacts?.offDays ?? 0;
                const manual = e.salaryOverride != null;
                return (
                  <tr key={r.employeeId}>
                    <td className="sb-gp-emp">
                      <div className="gm-emp">
                        <span className="gm-av" style={{ background: sbAvColor(e.id), width: 28, height: 28, fontSize: 10.5 }}>{sbInitials(e.name)}</span>
                        <span className="gm-empmeta">
                          <span className="gm-name">{e.name}</span>
                          <span className="gm-role">{e.code}</span>
                        </span>
                      </div>
                    </td>
                    <td className="sb-gp-sal sb-r gm-mono sb-hab">{fmtDA(e.referenceSalary)}</td>
                    <td className="sb-gp-sal sb-r gm-mono">
                      <b>{fmtDA(paid(e))}</b>
                      {manual && !sent && (
                        <span className="sb-gp-pen" title={e.computedSalary != null ? `Modifié à la main · calculé ${fmtDA(e.computedSalary)}` : 'Modifié à la main'}> ✎</span>
                      )}
                    </td>
                    <td className="sb-gp-sal sb-r">
                      {diff === 0
                        ? <span className="sb-ecart eq">=</span>
                        : <span className={'sb-ecart gm-mono ' + (diff < 0 ? 'dn' : 'up')}>{diff > 0 ? '+' : '−'}{fmtDA(Math.abs(diff))}</span>}
                    </td>
                    <td className={'sb-gp-sal sb-gp-sep sb-gp-off gm-mono' + (off ? '' : ' z')}>{off || '·'}</td>
                    {r.cells.map((c, i) => (
                      <td key={ent.days[i]!.date} className={'sb-gp-td' + (ent.days[i]!.isWeekend ? ' off' : '')}>
                        {c.type === 'we' || c.type === 'empty'
                          ? <span className={'sb-gc t-' + c.type} />
                          : <span className={'sb-gc gm-mono t-' + c.type}>{cellText(c)}</span>}
                      </td>
                    ))}
                    <td className="sb-gp-tot sb-r gm-mono">{hrs(r.hoursPaie)}</td>
                    {(['c', 'm', 'aj', 'anj'] as const).map((t) => {
                      const n = count(r.cells, t);
                      return <td key={t} className={'sb-gp-cnt gm-mono' + (n ? '' : ' z')}>{n || '·'}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="sb-gp-emp"><span className="gm-foot-lbl">Total</span></td>
                <td className="sb-gp-sal sb-r gm-mono sb-hab">{fmtDA(totRef)}</td>
                <td className="sb-gp-sal sb-r gm-mono"><b>{fmtDA(totPaid)}</b></td>
                <td className="sb-gp-sal sb-r">
                  {totPaid === totRef
                    ? <span className="sb-ecart eq">=</span>
                    : <span className={'sb-ecart gm-mono ' + (totPaid < totRef ? 'dn' : 'up')}>{totPaid > totRef ? '+' : '−'}{fmtDA(Math.abs(totPaid - totRef))}</span>}
                </td>
                <td className="sb-gp-sal sb-gp-sep sb-gp-off gm-mono">{sum((_, e) => e.monthFacts?.offDays ?? 0)}</td>
                <td colSpan={ent.days.length} />
                <td className="sb-gp-tot sb-r gm-mono">{hrs(sum((r) => r.hoursPaie))}</td>
                {(['c', 'm', 'aj', 'anj'] as const).map((t) => (
                  <td key={t} className="sb-gp-cnt gm-mono">{sum((r) => count(r.cells, t))}</td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="sb-gp-legend">
          <span><span className="sb-gc t-p">8h</span>Présent</span>
          <span><span className="sb-gc t-c">C</span>Congé (payé)</span>
          <span><span className="sb-gc t-m">M</span>Maladie</span>
          <span><span className="sb-gc t-aj">AJ</span>Absence justifiée</span>
          <span><span className="sb-gc t-anj">A</span>Non justifiée</span>
          <span><span className="sb-gc t-recup">R</span>Récup (payée)</span>
          <span><span className="sb-gc t-tt">TT</span>Télétravail (payé)</span>
          <span><span className="sb-gc t-h">F</span>Férié</span>
          <span><span className="sb-gc t-empty" />Hors contrat / à venir</span>
        </div>
      </div>
    </section>
  );
}

export function GrillePaieView({
  year,
  month,
  team,
  sent,
}: {
  year: number;
  month: number;
  team: PayrollEmployeeDTO[];
  sent: boolean;
}) {
  const q = usePayrollGrid({ year, month });
  const byId = new Map(team.map((e) => [e.id, e]));
  const entityIds = new Set(team.map((e) => e.entityId as string));

  if (q.isLoading) return <div className="sb-placeholder">Chargement de la grille paie…</div>;
  if (q.isError || !q.data) return <div className="sb-placeholder">Impossible de charger la grille paie — réessayez.</div>;

  const entities = q.data.entities.filter((e) => e.rows.some((r) => byId.has(r.employeeId)));
  const pending = q.data.pending.filter((p) => entityIds.has(p.entityId));

  return (
    <div className="sb-gpwrap">
      {entities.map((ent) => <EntityGrid key={ent.entityId} ent={ent} team={byId} sent={sent} />)}
      {pending.length > 0 && (
        <div className="sb-gp-locked">
          <span className="sb-gp-lockic" aria-hidden>🔒</span>
          <div>
            <b>{pending.length === 1 ? 'Grille pas encore validée' : 'Grilles pas encore validées'}</b>
            <p>
              {pending.map((p) => `${p.entityName} (${p.stageLabel})`).join(', ')}. La grille paie et le calcul des
              salaires s'affichent ici dès que la grille mensuelle est validée.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
